/** Чистые функции времени/дат для сетки расписания. */

export function parseTimeToMinutes(t: string): number {
  const parts = String(t).split(':');
  const h = parseInt(parts[0] ?? '0', 10);
  const m = parseInt(parts[1] ?? '0', 10);
  return h * 60 + m;
}

export function formatMinutes(total: number): string {
  const m = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Последняя минута суток: окончание слота не переходит через полночь. */
const LAST_MINUTE_OF_DAY = 24 * 60 - 1;

/**
 * Окончание слота = начало + длительность (мин). Не переходит через полночь (максимум 23:59).
 * null — если начало не задано или длительность не положительная.
 */
export function endTimeForDuration(start: string, durationMinutes: number): string | null {
  if (!/^\d{1,2}:\d{2}/.test(String(start))) return null;
  if (!Number.isFinite(durationMinutes) || durationMinutes < 1) return null;
  const end = Math.min(parseTimeToMinutes(start) + Math.round(durationMinutes), LAST_MINUTE_OF_DAY);
  return formatMinutes(end);
}

/** Длительность интервала в минутах; null — если окончание не позже начала или время не задано. */
export function intervalMinutes(start: string, end: string): number | null {
  if (!/^\d{1,2}:\d{2}/.test(String(start)) || !/^\d{1,2}:\d{2}/.test(String(end))) return null;
  const d = parseTimeToMinutes(end) - parseTimeToMinutes(start);
  return d > 0 ? d : null;
}

export function sliceTime(t: string): string {
  return String(t).slice(0, 5);
}

export function buildTimeRange(from: string, to: string, stepMin: number): string[] {
  const fromM = parseTimeToMinutes(from);
  let toM = parseTimeToMinutes(to);
  if (toM <= fromM) toM += 24 * 60;
  const out: string[] = [];
  for (let m = fromM; m < toM; m += stepMin) {
    out.push(formatMinutes(m));
  }
  return out;
}

export function minutesToPx(minutes: number, editFromM: number, pxPerMinute: number): number {
  return (minutes - editFromM) * pxPerMinute;
}

export function pxToMinutes(px: number, editFromM: number, pxPerMinute: number): number {
  return editFromM + px / pxPerMinute;
}

export function snapToStep(minutes: number, stepMin: number): number {
  return Math.floor(minutes / stepMin) * stepMin;
}

export function snapToStepNearest(minutes: number, stepMin: number): number {
  return Math.round(minutes / stepMin) * stepMin;
}

export function parseDate(ymd: string): Date {
  return new Date(`${ymd}T12:00:00Z`);
}

export function dateToYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getMonday(ymd: string): string {
  const d = parseDate(ymd);
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

export function getWeekDates(monday: string): string[] {
  const dates: string[] = [];
  const d = parseDate(monday);
  for (let i = 0; i < 7; i++) {
    dates.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return dates;
}

export const DEFAULT_SCHEDULE_TIMEZONE = 'Europe/Moscow';

function normalizeTimezone(tz?: string | null): string {
  const t = (tz ?? '').trim();
  if (!t) return DEFAULT_SCHEDULE_TIMEZONE;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: t });
    return t;
  } catch {
    return DEFAULT_SCHEDULE_TIMEZONE;
  }
}

/**
 * Текущая дата YYYY-MM-DD в целевом часовом поясе (по умолчанию Europe/Moscow).
 * Не зависит от локального времени компьютера.
 */
export function todayYmd(timezone?: string | null): string {
  const tz = normalizeTimezone(timezone);
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
    const m = parts.find((p) => p.type === 'month')?.value ?? '01';
    const d = parts.find((p) => p.type === 'day')?.value ?? '01';
    return `${y}-${m}-${d}`;
  } catch {
    return dateToYmd(new Date());
  }
}

/**
 * Текущие минуты от полуночи в целевом часовом поясе (0..1439.99).
 */
export function nowMinutesInTimezone(timezone?: string | null): number {
  const tz = normalizeTimezone(timezone);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
    const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
    const s = parseInt(parts.find((p) => p.type === 'second')?.value ?? '0', 10);
    return h * 60 + m + s / 60;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
  }
}

/**
 * Проверка: находится ли время слота в прошлом в указанном часовом поясе.
 */
export function isSlotStartInPast(
  slotDate: string,
  startTime: string,
  timezone?: string | null,
): boolean {
  const tz = normalizeTimezone(timezone);
  const today = todayYmd(tz);
  if (slotDate < today) return true;
  if (slotDate > today) return false;
  const startM = parseTimeToMinutes(startTime);
  const nowM = nowMinutesInTimezone(tz);
  return startM < nowM;
}

/**
 * Начало следующей ячейки сетки (кратной step с полуночи), строго после «сейчас» в целевом часовом поясе.
 * Согласовано с продуктовым правилом «не раньше следующей отметки» на клиенте до вызова API переноса.
 */
export function nextGridSlotStartAfterNow(stepMinutes: number, timezone?: string | null): { date: string; startM: number } {
  const step = Math.max(1, Math.min(1440, Math.floor(stepMinutes)));
  const tz = normalizeTimezone(timezone);
  const curYmd = todayYmd(tz);
  const curM = nowMinutesInTimezone(tz);
  const idx = Math.floor(curM / step) + 1;
  const nextM = idx * step;
  if (nextM < 24 * 60) {
    return { date: curYmd, startM: nextM };
  }
  // Переход через полночь на следующий день
  const d = parseDate(curYmd);
  d.setUTCDate(d.getUTCDate() + 1);
  return { date: d.toISOString().slice(0, 10), startM: nextM % (24 * 60) };
}

export interface LaneItem {
  startM: number;
  endM: number;
  lane?: number;
  maxLanes?: number;
}

export function mergeIntervals(intervals: Array<{ s: number; e: number }>): Array<{ s: number; e: number }> {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((a, b) => a.s - b.s);
  const first = sorted[0];
  if (first === undefined) return [];
  const merged: Array<{ s: number; e: number }> = [{ s: first.s, e: first.e }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    if (cur === undefined) continue;
    const last = merged[merged.length - 1];
    if (last === undefined) break;
    if (cur.s <= last.e) {
      last.e = Math.max(last.e, cur.e);
    } else {
      merged.push({ s: cur.s, e: cur.e });
    }
  }
  return merged;
}

export function assignLanes<T extends LaneItem>(items: T[]): T[] {
  if (!items.length) return items;

  const sorted = [...items].sort((a, b) =>
    a.startM !== b.startM ? a.startM - b.startM : a.endM - b.endM,
  );

  const activeByLane: (number | null)[] = [];
  for (const item of sorted) {
    for (let i = 0; i < activeByLane.length; i++) {
      if (activeByLane[i] !== null && (activeByLane[i] as number) <= item.startM) {
        activeByLane[i] = null;
      }
    }
    let lane = activeByLane.findIndex((laneEnd) => laneEnd === null);
    if (lane === -1) {
      lane = activeByLane.length;
      activeByLane.push(item.endM);
    } else {
      activeByLane[lane] = item.endM;
    }
    item.lane = lane;
  }

  let clusterStart = 0;
  while (clusterStart < sorted.length) {
    const head = sorted[clusterStart];
    if (head === undefined) break;
    let clusterEndM = head.endM;
    let clusterMaxLane = (head.lane ?? 0) + 1;
    let i = clusterStart + 1;

    while (i < sorted.length) {
      const cur = sorted[i];
      if (cur === undefined || cur.startM >= clusterEndM) break;
      clusterEndM = Math.max(clusterEndM, cur.endM);
      clusterMaxLane = Math.max(clusterMaxLane, (cur.lane ?? 0) + 1);
      i++;
    }

    for (let j = clusterStart; j < i; j++) {
      const cur = sorted[j];
      if (cur !== undefined) {
        cur.maxLanes = Math.max(1, clusterMaxLane);
      }
    }
    clusterStart = i;
  }

  for (const item of sorted) {
    if (item.maxLanes === undefined) item.maxLanes = 1;
  }

  return items;
}

export function hexToRgba(hex: string, alpha: number): string | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  const hexPart = m?.[1];
  if (!hexPart) return null;
  const r = parseInt(hexPart.slice(0, 2), 16);
  const g = parseInt(hexPart.slice(2, 4), 16);
  const b = parseInt(hexPart.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Нормализация цвета услуги/типа из API (#RGB, RRGGBB без #) для градиента слота в календаре. */
export function normalizeScheduleAccentHex(s: string | null | undefined): string | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (!t) return null;
  const raw = t.startsWith('#') ? t.slice(1) : t;
  if (!/^[0-9a-fA-F]+$/.test(raw)) return null;
  let six = raw;
  if (raw.length === 3) {
    six = raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2];
  }
  if (six.length !== 6) return null;
  return `#${six.toLowerCase()}`;
}

export function isValidHexColor(s: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(s);
}
